import * as OWUtils from '~/utils/OWUtils'
import { CustomerType } from '~/generated/types'

let _indexInstance: CustomerSearchIndex | null = null

type Doc = { token: string; field: string }

export class CustomerSearchIndex {
    private docs: Record<string, { customer: CustomerType; docs: Doc[] }>
    private exactMatchDocs: Record<string, CustomerType>

    constructor() {
        this.docs = {}
        this.exactMatchDocs = {}
    }

    static fields = ['name']
    static listFields = ['aliases']

    static getInstance() {
        if (!_indexInstance) {
            _indexInstance = new CustomerSearchIndex()
        }
        return _indexInstance
    }

    getTokens(s) {
        return s
            .toLocaleLowerCase()
            .split(/\s+/g)
            .filter(Boolean)
            .map(s => OWUtils.removeAccents(s))
    }

    getDocs(e) {
        const docs: Doc[] = []
        CustomerSearchIndex.fields.forEach(f => {
            const tokens = this.getTokens(e[f] || '').map(t => ({
                token: t,
                field: f,
            }))
            Array.prototype.push.apply(docs, tokens)
        })
        CustomerSearchIndex.listFields.forEach(f => {
            const aliasString = e[f].reduce((accumulator, currentValue) => {
                return accumulator + currentValue + ' '
            }, '')
            const tokens = this.getTokens(aliasString || '').map(t => ({
                token: t,
                field: f,
            }))
            Array.prototype.push.apply(docs, tokens)
        })
        return docs.sort(({ token: token1 }, { token: token2 }) => token1.length - token2.length)
    }

    // Must match the name or aliases exactly
    getExactMatchDocs(e) {
        const exactMatchDocs: Doc[] = []
        CustomerSearchIndex.fields.forEach(f => {
            exactMatchDocs.push({ field: f, token: e[f] })
        })
        CustomerSearchIndex.listFields.forEach(f => {
            const tokens: any = []
            e[f].forEach(listItem => {
                tokens.push({ field: f, token: listItem })
            })
            exactMatchDocs.push(...tokens)
        })
        return exactMatchDocs.sort(({ token: token1 }, { token: token2 }) => token1.length - token2.length)
    }

    insertCustomer(customer) {
        this.docs[customer.customerId] = { customer: customer, docs: this.getDocs(customer) }
        this.getExactMatchDocs(customer).forEach(match => {
            this.exactMatchDocs[match.token.toLowerCase().trim()] = customer
        })
    }

    search(query): CustomerType[] {
        const queryTokens = this.getTokens(query || '')
        const qLen = queryTokens.length
        return (
            Object.values(this.docs)
                .map(e => {
                    let exactC = 0
                    let fail = false
                    for (let i = 0; i < queryTokens.length && !fail; i++) {
                        const qt = queryTokens[i]
                        const d = e.docs.find(({ token }) => token.startsWith(qt))
                        if (d) {
                            if (d.token === qt) {
                                exactC++
                            }
                        } else {
                            fail = true
                        }
                    }
                    return { doc: e, exact: exactC === qLen, fail }
                })
                // Filter
                .filter(({ fail }) => !fail)
                // Sort
                .sort((a, b) => {
                    if (a.exact && !b.exact) {
                        return -1
                    } else if (!a.exact && b.exact) {
                        return 1
                    } else {
                        return a.doc.customer.name < b.doc.customer.name ? -1 : 1
                    }
                })
                // Convert to customers
                .map(({ doc }) => doc.customer)
        )
    }

    searchExact(query: string): CustomerType | undefined {
        return this.exactMatchDocs[query.toLowerCase().trim()]
    }
}
